import React, { useState, useCallback, useEffect } from "react";
import { Container, Row, Col, Modal, Card, CardBody, Label, Input } from "reactstrap";
import MetaTags from "react-meta-tags";
import { showSuccessAlert, showErrorAlert } from "pages/utils/Alerts/alertMessages";
import { get, post } from "helpers/api_helper";

const GATEWAY_LOGOS = {
    "Razorpay": "bx-credit-card-front",
    "PayU": "bx-wallet",
    "Cashfree": "bx-money",
    "PayPal": "bxl-paypal",
    "Skydo": "bx-transfer-alt",
    "Stripe": "bx-credit-card",
    "Checkout.com": "bx-badge-check",
    "GoCardless": "bx-buildings",
    "Braintree": "bx-globe",
    "PayPal Payments Pro": "bxl-paypal",
    "BlueSnap": "bx-shopping-bag",
    "Worldpay": "bx-lock-alt"
};

const PaymentGateway = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [formData, setFormData] = useState({
        id: "", gateway_name: "", is_active: false, environment: "Test",
        credentials: { key_id: "", key_secret: "", webhook_secret: "", client_id: "" },
        supported_currencies: [], payment_methods: []
    });
    const [paymentGatewayList, setPaymentGatewayList] = useState([]);

    const fetchPaymentGateways = useCallback(async () => {
        try {
            const res = await get("/superadmin/payment-gateways");
            const data = res?.data || res;
            if (Array.isArray(data) && data.length > 0) {
                setPaymentGatewayList(data);
            }
        } catch (e) {
            console.warn("Using fallback payment gateway data", e);
        }
    }, []);

    useEffect(() => {
        fetchPaymentGateways();
    }, [fetchPaymentGateways]);

    const toggleModal = useCallback(() => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setSelectedData(null);
        }
    }, [modalOpen]);

    const handleEditClick = (gateway) => {
        setSelectedData(gateway);
        setFormData({
            id: gateway.id,
            gateway_name: gateway.gateway_name,
            is_active: gateway.is_active,
            environment: gateway.environment || "Test",
            credentials: {
                key_id: gateway.credentials?.key_id || "",
                key_secret: gateway.credentials?.key_secret || "",
                webhook_secret: gateway.credentials?.webhook_secret || "",
                client_id: gateway.credentials?.client_id || ""
            },
            supported_currencies: gateway.supported_currencies || [],
            payment_methods: gateway.payment_methods || []
        });
        setModalOpen(true);
    };

    const handleToggleActive = async (gateway) => {
        try {
            const updatedState = !gateway.is_active;
            await post("/superadmin/payment-gateways", {
                id: gateway.id,
                is_active: updatedState
            });
            showSuccessAlert(`${gateway.gateway_name} status updated successfully!`);
            fetchPaymentGateways();
        } catch (e) {
            showErrorAlert("Failed to update status.");
        }
    };

    const handleCredentialChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            credentials: {
                ...prev.credentials,
                [name]: value
            }
        }));
    };

    const handleCurrencyToggle = (currency) => {
        setFormData(prev => {
            const current = [...prev.supported_currencies];
            const idx = current.indexOf(currency);
            if (idx > -1) {
                current.splice(idx, 1);
            } else {
                current.push(currency);
            }
            return { ...prev, supported_currencies: current };
        });
    };

    const handleMethodToggle = (method) => {
        setFormData(prev => {
            const current = [...prev.payment_methods];
            const idx = current.indexOf(method);
            if (idx > -1) {
                current.splice(idx, 1);
            } else {
                current.push(method);
            }
            return { ...prev, payment_methods: current };
        });
    };

    const handleSubmit = async () => {
        try {
            await post("/superadmin/payment-gateways", formData);
            showSuccessAlert("Payment Gateway saved successfully!");
            fetchPaymentGateways();
            toggleModal();
        } catch (e) {
            showErrorAlert("Failed to save gateway config.");
        }
    };

    return (
        <React.Fragment>
            <div className="dash-page ms-4 me-4">
                <MetaTags>
                    <title>Payment Gateway | {process.env.REACT_APP_TITLE}</title>
                </MetaTags>
                <Container fluid>
                    <div className="p-3">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <div>
                                <h4 className="mb-1 fw-bold text-dark">Payment Gateways</h4>
                                <p className="text-muted mb-0">Configure, activate, and manage your sandbox and live gateway integrations</p>
                            </div>
                        </div>

                        <Row className="g-4">
                            {paymentGatewayList.map((gw) => {
                                const logoClass = GATEWAY_LOGOS[gw.gateway_name] || "bx-credit-card";
                                return (
                                    <Col xl="3" lg="4" md="6" key={gw.id}>
                                        <Card className="h-100 shadow-sm border border-light" style={{ borderRadius: "12px", transition: "transform 0.2s" }}>
                                            <CardBody className="p-4 d-flex flex-column justify-content-between">
                                                <div>
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="avatar-sm rounded-circle d-flex align-items-center justify-content-center text-primary"
                                                                style={{ width: "42px", height: "42px", background: "#f0f4fc", fontSize: "22px" }}>
                                                                <i className={`bx ${logoClass}`} />
                                                            </div>
                                                            <div>
                                                                <h5 className="mb-0 fw-bold text-dark">{gw.gateway_name}</h5>
                                                                <span className="small text-muted">{gw.environment || "Test"} Mode</span>
                                                            </div>
                                                        </div>
                                                        <div className="form-check form-switch p-0 m-0">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                id={`switch-${gw.id}`}
                                                                checked={gw.is_active}
                                                                onChange={() => handleToggleActive(gw)}
                                                                style={{ cursor: "pointer", width: "38px", height: "20px" }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <hr className="my-3" />

                                                    <div className="mb-2">
                                                        <span className="small text-muted d-block mb-1">Supported Currencies</span>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {gw.supported_currencies?.length > 0 ? (
                                                                gw.supported_currencies.map((c) => (
                                                                    <span key={c} className="badge bg-secondary-subtle text-secondary small">{c}</span>
                                                                ))
                                                            ) : (
                                                                <span className="text-muted small">—</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="mb-3">
                                                        <span className="small text-muted d-block mb-1">Active Methods</span>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {gw.payment_methods?.length > 0 ? (
                                                                gw.payment_methods.map((m) => (
                                                                    <span key={m} className="badge bg-primary-subtle text-primary small text-capitalize">{m}</span>
                                                                ))
                                                            ) : (
                                                                <span className="text-muted small">—</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    className="btn btn-outline-primary w-100 py-2 mt-2 fw-semibold"
                                                    style={{ border: "1px solid #243E79", color: "#243E79" }}
                                                    onClick={() => handleEditClick(gw)}
                                                >
                                                    <i className="bx bx-edit-alt me-1" /> Configure Credentials
                                                </button>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>

                    <Modal isOpen={modalOpen} toggle={toggleModal} size="lg" className="custom-modal">
                        <div className="modal-header">
                            <h5 className="modal-title fw-bold mb-1">Configure {formData.gateway_name}</h5>
                            <button type="button" onClick={toggleModal} className="close border-0 bg-transparent" aria-label="Close">
                                <i className="bx bx-x h3 m-0" />
                            </button>
                        </div>

                        <div className="modal-body p-4">
                            <Row className="g-3">
                                <Col md="6">
                                    <Label className="fw-semibold small">Environment</Label>
                                    <select
                                        className="form-select py-2"
                                        value={formData.environment}
                                        onChange={(e) => setFormData(prev => ({ ...prev, environment: e.target.value }))}
                                    >
                                        <option value="Test">Sandbox (Test Mode)</option>
                                        <option value="Live">Production (Live Mode)</option>
                                    </select>
                                </Col>

                                <Col md="6">
                                    <Label className="fw-semibold small">Client ID / Key ID</Label>
                                    <Input
                                        type="text"
                                        name="key_id"
                                        className="py-2"
                                        value={formData.credentials.key_id}
                                        onChange={handleCredentialChange}
                                        placeholder="Enter client public identifier key"
                                    />
                                </Col>

                                <Col md="6">
                                    <Label className="fw-semibold small">Key Secret / Client Secret</Label>
                                    <Input
                                        type="password"
                                        name="key_secret"
                                        className="py-2"
                                        value={formData.credentials.key_secret}
                                        onChange={handleCredentialChange}
                                        placeholder="Enter key secret string"
                                    />
                                </Col>

                                <Col md="6">
                                    <Label className="fw-semibold small">Webhook Signing Secret</Label>
                                    <Input
                                        type="password"
                                        name="webhook_secret"
                                        className="py-2"
                                        value={formData.credentials.webhook_secret}
                                        onChange={handleCredentialChange}
                                        placeholder="Enter webhook verification key"
                                    />
                                </Col>

                                <Col md="12" className="mt-4">
                                    <Label className="fw-semibold small d-block mb-2">Supported Currencies</Label>
                                    <div className="d-flex gap-3">
                                        {["USD", "EUR", "INR", "GBP", "CAD", "AED"].map((currency) => (
                                            <Label key={currency} className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                                                <Input
                                                    type="checkbox"
                                                    checked={formData.supported_currencies.includes(currency)}
                                                    onChange={() => handleCurrencyToggle(currency)}
                                                />
                                                <span>{currency}</span>
                                            </Label>
                                        ))}
                                    </div>
                                </Col>

                                <Col md="12" className="mt-4">
                                    <Label className="fw-semibold small d-block mb-2">Enabled Payment Methods</Label>
                                    <div className="d-flex flex-wrap gap-4">
                                        {["card", "upi", "netbanking", "bank_debit", "paypal", "sepa", "alipay"].map((method) => (
                                            <Label key={method} className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
                                                <Input
                                                    type="checkbox"
                                                    checked={formData.payment_methods.includes(method)}
                                                    onChange={() => handleMethodToggle(method)}
                                                />
                                                <span className="text-capitalize">{method.replace("_", " ")}</span>
                                            </Label>
                                        ))}
                                    </div>
                                </Col>
                            </Row>

                            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                <button className="btn btn-light" style={{ width: "100px" }} onClick={toggleModal}>Cancel</button>
                                <button className="btn btn-primary px-4" onClick={handleSubmit} style={{ background: "#243E79", borderColor: "#243E79", width: "140px" }}>
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </Modal>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default PaymentGateway;
